import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trash2,
  MessageSquarePlus,
  X,
  Star,
  MessageSquare,
  CheckCircle2,
  Users,
  Loader2,
  AlertCircle
} from "lucide-react";
import Skeleton from "../components/Skeleton";
import "../styles/Roster.css";
import "../styles/TestimonialAdmin.css";

const TestimonialAdmin = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    author: "",
    text: "",
    platform: "Google",
    rating: 5,
  });

  const platforms = ["Google", "LinkedIn", "TrustPilot", "G2", "Yelp", "Facebook", "Clutch"];

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Ensure we are targeting the public schema explicitly if needed
      const { data, error: fetchError } = await supabase
        .from("testimonials")
        .select("*")
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setReviews(data || []);
    } catch (err) {
      console.error("Supabase Fetch Error:", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error: insertError } = await supabase.from("testimonials").insert([formData]);
      if (insertError) throw insertError;

      setIsModalOpen(false);
      setFormData({ author: "", text: "", platform: "Google", rating: 5 });
      await fetchReviews();
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      alert("Error saving testimonial: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteReview = async (id) => {
    if (window.confirm("Remove this testimonial from public view?")) {
      const { error: delError } = await supabase.from("testimonials").delete().eq("id", id);
      if (!delError) fetchReviews();
    }
  };

  return (
    <div className="rni-portal-view">
      <header className="roster-header">
        <div className="header-info">
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800 }}>Wall of Love</h1>
          <p>Managing {reviews.length} public testimonials</p>
        </div>
        <button className="add-agent-btn" onClick={() => setIsModalOpen(true)}>
          <MessageSquarePlus size={19} /> Add Review
        </button>
      </header>

      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>Error connecting to Supabase: {error}. Check RLS policies for table 'testimonials'.</span>
        </div>
      )}

      <div className="roster-container">
        <div className="table-wrapper">
          <table className="agent-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Author</th>
                <th>Review Snippet</th>
                <th>Platform</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td><Skeleton type="s-rank" /></td>
                    <td><Skeleton type="s-name" /></td>
                    <td><Skeleton type="s-name" /></td>
                    <td><Skeleton type="s-status" /></td>
                    <td className="text-right"><Skeleton type="s-rank" /></td>
                  </tr>
                ))
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No testimonials found in the database.
                  </td>
                </tr>
              ) : (
                reviews.map((rev, index) => (
                  <motion.tr key={rev.id}>
  <td><span className="number-badge">{index + 1}</span></td>
  <td><span className="main-name" style={{ fontWeight: '700' }}>{rev.author}</span></td>
  <td className="sub-text">"{rev.text.substring(0, 50)}..."</td>
  <td>
    <span className={`platform-badge ${rev.platform.toLowerCase()}`}>
      {rev.platform}
    </span>
  </td>
  <td className="text-right">
    <button className="rni-delete-btn" onClick={() => deleteReview(rev.id)}>
      <Trash2 size={16} />
    </button>
  </td>
</motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal and Toast logic remains same but uses refined classes */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="modal-overlay">
            <motion.div className="modal-box preview-layout" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <button className="custom-close-btn" onClick={() => setIsModalOpen(false)}><X size={20} /></button>
              <div className="modal-content-grid">
                <div className="preview-section">
                  <h3 style={{fontFamily: 'Plus Jakarta Sans'}}>Live Preview</h3>
                  <div className="preview-card">
                    <div className="preview-info">
                      <div style={{ display: "flex", gap: "4px", marginBottom: "8px" }}>
                        {[...Array(Number(formData.rating))].map((_, i) => (
                          <Star key={i} size={14} fill="#fbbf24" color="#fbbf24" />
                        ))}
                      </div>
                      <p className="preview-text">"{formData.text || "Your review content will appear here..."}"</p>
                      <h4>{formData.author || "Author Name"}</h4>
                      <small>{formData.platform} Review</small>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="admin-form">
                  <div className="modal-header-text">
                    <h2>New Testimonial</h2>
                    <p>Add social proof to your wall.</p>
                  </div>
                  <div className="form-group">
                    <label>Author Name</label>
                    <div className="input-with-icon">
                      <Users size={17} className="field-icon" />
                      <input required placeholder="e.g. John Doe" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Review Content</label>
                    <div className="input-with-icon">
                      <MessageSquare size={17} className="field-icon" style={{ top: "15px", transform: "none" }} />
                      <textarea required placeholder="What did they say?" value={formData.text} onChange={(e) => setFormData({ ...formData, text: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row" style={{ display: "flex", gap: "12px" }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Platform</label>
                      <select value={formData.platform} onChange={(e) => setFormData({ ...formData, platform: e.target.value })}>
                        {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ width: "100px" }}>
                      <label>Rating</label>
                      <input type="number" max="5" min="1" value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: e.target.value })} />
                    </div>
                  </div>
                  <button type="submit" className="submit-btn" disabled={submitting}>
                    {submitting ? <Loader2 className="spinner" size={18} /> : "Publish Testimonial"}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showToast && (
          <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="success-toast-green">
            <CheckCircle2 size={20} /> <span>Testimonial Published Successfully</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TestimonialAdmin;